import React, { useContext, useEffect, useMemo, useState } from "react";
// import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import Logo from "../assets/Img/Logo-1.png";
import User from "../assets/Img/User.gif";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RedWorkerTimeModal from "./RedWorkerTimeModal";

const isActiveRedNotification = note => {
  const activeSlotStatuses = new Set(["PUBLISHED", "HOME_APPROVAL", "SELECTION", "CONFIRMED", "REPORTING", "STARTED"]);
  const closedWorkerStatuses = new Set(["HOME_DECLINED", "RELEASED", "CANCELLED"]);
  const expiryStatuses = new Set(["PUBLISHED", "HOME_APPROVAL", "SELECTION"]);
  const slotStatus = String(note.Status || "").toUpperCase();
  const workerStatus = String(note.interest_status || "").toUpperCase();
  const scheduledEnd = note.ScheduledEnd ? new Date(note.ScheduledEnd).getTime() : null;
  const expired = expiryStatuses.has(slotStatus) && Number.isFinite(scheduledEnd) && scheduledEnd <= Date.now();
  return activeSlotStatuses.has(slotStatus) && !closedWorkerStatuses.has(workerStatus) && !expired;
};

const formatDateForApi = value => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const redSlotHasTimedOut = note => {
  const scheduledEnd=note.ScheduledEnd?new Date(note.ScheduledEnd).getTime():NaN;
  return Number.isFinite(scheduledEnd)&&scheduledEnd<=Date.now();
};

const isRedInterestWindowOpen = note => {
  const slotStatus=String(note.Status||"").toUpperCase();
  return ["PUBLISHED","HOME_APPROVAL"].includes(slotStatus)&&!redSlotHasTimedOut(note);
};

const redStatusLabel = status => ({
  OPEN: "Kaam ke liye haan bolein",
  INTERESTED: "Jawab bhej diya",
  HOME_APPROVED: "Supervisor ne haan ki",
  HOME_DECLINED: "Supervisor ne mana kiya",
  SELECTED: "Aap select ho gaye",
  STANDBY: "Backup worker",
  RELEASED: "Duty se hata diya",
  PUBLISHED: "Worker chahiye",
  HOME_APPROVAL: "Supervisor ka jawab baki",
  SELECTION: "Worker selection baki",
  CONFIRMED: "Kaam pakka",
  REPORTING: "Pahunch confirm karein",
  STARTED: "Kaam chal raha hai",
  QUALITY_PENDING: "Kaam pura, checking baki",
  APPROVED: "Kaam pura",
  CANCELLED: "Kaam radd",
}[String(status || "").toUpperCase()] || String(status || "-").replaceAll("_", " "));

const redVideoUrl = value => {
  const path=String(value||"").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base=String(process.env.REACT_APP_API_BASE_PATH||axiosInstance.defaults.baseURLPath||axiosInstance.defaults.baseURL||window.location.origin).replace(/\/+$/,"");
  return `${base}${path.startsWith("/")?"":"/"}${path}`;
};

const isRedApplicationFull = note => {
  const used = Number(note.ActiveApplicationCount);
  const capacity = Number(note.ApplicationCapacity);
  return Number.isFinite(used) && Number.isFinite(capacity) && capacity > 0 && used >= capacity;
};

const redWorkerInstruction = note => {
  if (note.Title === "RED Time Update Required") return note.Message || "Supervisor ne naya available time select karne ko kaha hai.";
  const status = String(note.interest_status || note.Status || "OPEN").toUpperCase();
  if (!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(String(note.Status || "").toUpperCase())) {
    if (redSlotHasTimedOut(note)) return "Is slot ka available time khatam ho gaya hai. Ab Interested request nahi bheji ja sakti.";
    if (isRedApplicationFull(note)) return `Is lot ke liye ${note.ActiveApplicationCount} workers pehle hi haan bol chuke hain. Kripya wait karein. Supervisor kisi worker ko decline karega to aapke liye jagah khul sakti hai.`;
    return "Is lot ke liye worker chahiye. Kaam karna hai to neeche 'Mujhe Kaam Karna Hai' dabayein.";
  }
  if (status === "INTERESTED") return "Aapne haan bol diya hai. Ab supervisor ke jawab ka wait karein.";
  if (status === "HOME_APPROVED") return "Aapke supervisor ne haan kar di hai. Final selection ka wait karein.";
  if (status === "SELECTED") return "Aap is kaam ke liye select ho gaye hain. Diye gaye time par report karein.";
  if (status === "STANDBY") return "Aap backup worker hain. Supervisor ke bulane ka wait karein.";
  if (status === "STARTED") return "Kaam shuru ho gaya hai.";
  if (["APPROVED", "QUALITY_PENDING"].includes(status)) return "Yeh kaam pura ho gaya hai.";
  if (["HOME_DECLINED", "RELEASED", "CANCELLED"].includes(status)) return "Is kaam par ab koi action nahi lena hai.";
  return note.Message || "Kaam ki nayi jankari.";
};

const safeNotificationDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",hour12:true}).format(date).replace(/\b(am|pm)\b/gi,value=>value.toUpperCase());
};

function UserDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [department, setDepartment] = useState(user?.Department || "");
  const [canViewWorkbookLots, setCanViewWorkbookLots] = useState(false);
  const [temporaryDepartment, setTemporaryDepartment] =
    useState("Not Assigned");
  // Data states
  const [workerData, setWorkerData] = useState([]); // final data after item filtering
  const [uniqueItems, setUniqueItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("All");
  //css
  const [hover, setHover] = useState(false);
  // Pagination and totals
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Number of items per page for mobile view
  const [totalAmount, setTotalAmount] = useState(0);
  const [salarySummary, setSalarySummary] = useState({
    CompanyWorkAmount: 0,
    ReworkAmount: 0,
    GrossWorkAmount: 0,
    TotalEarned: 0,
    TotalQty: 0,
    AdvanceDeduction: 0,
    AdvancesDeducted: 0,
    TicketDeduction: 0,
    Penalties: 0,
    DebitFormDeduction: 0,
    TotalDeduction: 0,
    OutstandingAdvanceBalance: 0,
    FinalNetPayable: 0,
    PaymentStatus: "Loading...",
    AdvanceDetails: [],
    TicketDetails: [],
    DebitFormDetails: [],
  });
  const [isSalaryLoading, setIsSalaryLoading] = useState(true);
  const formatCurrency = (value) =>
    `₹${Math.abs(Number(value) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  // Date filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalQty, setTotalQty] = useState(0);

  const [lotWiseData, setLotWiseData] = useState([]);
  const [allLotWiseData, setAllLotWiseData] = useState([]);
  const [redSupportNotifications, setRedSupportNotifications] = useState([]);
  const [redNotificationModalOpen, setRedNotificationModalOpen] = useState(false);
  const [redNotificationView, setRedNotificationView] = useState("active");
  const [redNotificationSearch, setRedNotificationSearch] = useState("");
  const [redTimeModal, setRedTimeModal] = useState(null);

  const latestRedNotifications = useMemo(() => {
    const latestBySlot = new Map();
    redSupportNotifications.forEach(note => {
      const key = String(note.Id || note.SlotId || note.notification_id);
      if (!latestBySlot.has(key)) latestBySlot.set(key, note);
    });
    return [...latestBySlot.values()];
  }, [redSupportNotifications]);

  const activeRedNotifications = useMemo(() => {
    return latestRedNotifications.filter(isActiveRedNotification);
  }, [latestRedNotifications]);

  const redNotificationHistory = useMemo(() => {
    const activeIds = new Set(activeRedNotifications.map(note => note.notification_id));
    return latestRedNotifications.filter(note => !activeIds.has(note.notification_id));
  }, [activeRedNotifications, latestRedNotifications]);

  const unreadRedNotificationCount = redSupportNotifications.filter(note => isActiveRedNotification(note) && !note.IsRead).length;
  const currentRedNotifications = redNotificationView === "active" ? activeRedNotifications : redNotificationHistory;
  const visibleRedNotifications = useMemo(() => {
    const query = redNotificationSearch.trim().toLowerCase();
    if (!query) return currentRedNotifications;
    return currentRedNotifications.filter(note => [
      note.DisplayLotId, note.LotId, note.Department, note.ProcessName, note.Message,
      note.Status, note.interest_status, note.CrewRole, note.ReceivingSupervisor,
    ].some(value => String(value || "").toLowerCase().includes(query)));
  }, [currentRedNotifications, redNotificationSearch]);

  const loadRedSupportNotifications = async () => {
    if (!user?.EmployeeID) return;
    try {
      const response = await axiosInstance.get(`/api/red-support-slots/worker/${user.EmployeeID}`);
      setRedSupportNotifications(response.data.notifications || []);
    } catch (error) { console.error("Red support notification fetch error:", error); }
  };
  useEffect(() => {
    loadRedSupportNotifications();
    const timer = setInterval(loadRedSupportNotifications, 30000);
    return () => clearInterval(timer);
  }, [user?.EmployeeID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    const checkWorkbookDepartment = async () => {
      if (!String(department || "").trim()) {
        if (active) setCanViewWorkbookLots(false);
        return;
      }
      try {
        const response = await axiosInstance.get("/api/foam-cutting-sharepoint/lots", {
          params: { department },
        });
        if (active) setCanViewWorkbookLots((response.data?.lots || []).length > 0);
      } catch (error) {
        if (active) setCanViewWorkbookLots(false);
      }
    };
    checkWorkbookDepartment();
    return () => { active = false; };
  }, [department]);
  const registerInterest = slot => setRedTimeModal({ slot, mode: "interest" });
  const changeRedAvailability = slot => setRedTimeModal({ slot, mode: "update" });

  const handleRedTimeSaved = async ({ selectedSlot, selectedStart, mode }) => {
    if (mode === "update") {
      setRedSupportNotifications(current => current.map(item => String(item.Id) === String(redTimeModal?.slot?.Id) ? {
        ...item,
        PreferredScheduledStart: selectedStart,
        PreferredTimeSlots: [{ start: selectedStart, end: selectedSlot?.end }],
      } : item));
    }
    await loadRedSupportNotifications();
  };

  const openRedNotifications = async () => {
    setRedNotificationView(activeRedNotifications.length ? "active" : redNotificationHistory.length ? "history" : "active");
    setRedNotificationSearch("");
    setRedNotificationModalOpen(true);
    const unreadIds = redSupportNotifications.filter(note => isActiveRedNotification(note) && !note.IsRead).map(note => note.notification_id);
    if (!unreadIds.length) return;
    setRedSupportNotifications(current => current.map(note => unreadIds.includes(note.notification_id) ? { ...note, IsRead: true } : note));
    await Promise.allSettled(unreadIds.map(id => axiosInstance.patch(`/api/red-support-slots/notifications/${id}/read`)));
  };

  const renderRedNotification = note => {
    const workerStatus = note.interest_status || (note.Status === "PUBLISHED" ? "OPEN" : note.Status) || "OPEN";
    const receivedAt = safeNotificationDate(note.CreatedAt);
    const assignedAvailability=(note.PreferredTimeSlots||[]).find(item=>new Date(item.start).getTime()===new Date(note.PreferredScheduledStart).getTime());
    const scheduledAt = safeNotificationDate(note.PreferredScheduledStart||note.ScheduledStart);
    const scheduledEndAt = safeNotificationDate(assignedAvailability?.end||note.ScheduledEnd);
    const preferredTimes=(note.PreferredTimeSlots||[]).map(item=>safeNotificationDate(item.start)).filter(Boolean);
    const workerChoosesTime=!['SELECTED','STANDBY'].includes(String(note.interest_status||'').toUpperCase());
    const interestWindowOpen=isRedInterestWindowOpen(note);
    const timedOut=redSlotHasTimedOut(note);
    const badgeClass = ["HOME_DECLINED", "CANCELLED", "RELEASED"].includes(workerStatus)
      ? "bg-danger"
      : ["SELECTED", "STARTED", "APPROVED"].includes(workerStatus)
        ? "bg-success"
        : "bg-warning text-dark";
    return <article className="rounded-4 p-3 p-sm-4 mb-3 bg-white shadow-sm border border-start border-4 border-danger" key={note.notification_id}>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-start gap-2 mb-3">
        <div>
          <div className="d-flex align-items-center gap-2"><span className="rounded-circle bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center" style={{ width: 34, height: 34 }}><i className="bi bi-box-seam"></i></span><h5 className="mb-0">Lot {note.DisplayLotId || note.LotId || "Not available"}</h5></div>
          {receivedAt && <div className="small text-muted mt-1 ms-sm-5">Suchna mili: {receivedAt}</div>}
        </div>
        <span className={`badge rounded-pill px-3 py-2 align-self-start ${badgeClass}`}>{redStatusLabel(workerStatus)}</span>
      </div>
      <div className="row g-2 mb-3">
        <div className="col-12 col-md-5"><div className="bg-light rounded-3 p-2 h-100"><div className="small text-muted">Department</div><strong className="small">{note.Department || "RED Process"}</strong></div></div>
        <div className="col-6 col-md-3"><div className="bg-light rounded-3 p-2 h-100"><div className="small text-muted">Kaam ki sthiti</div><strong className="small">{redStatusLabel(note.Status)}</strong></div></div>
        <div className="col-6 col-md-4"><div className="bg-light rounded-3 p-2 h-100"><div className="small text-muted">Work Time</div>{workerChoosesTime?<><strong className="small d-block">{preferredTimes.length?preferredTimes.map(value=><span className="d-block" key={value}>• {value}</span>):"Interested par ek available time select karein"}</strong><span className="small text-muted d-block">Crew selection par aapka allocated time confirm hoga.</span></>:<><strong className="small d-block">Start: {scheduledAt || "Time nahi diya"}</strong><span className="small text-muted d-block">End: {scheduledEndAt || "Time nahi diya"}</span></>}{!note.interest_status&&["PUBLISHED","HOME_APPROVAL"].includes(String(note.Status||"").toUpperCase())&&<span className={`badge mt-2 ${interestWindowOpen?"bg-success":"bg-secondary"}`}>{interestWindowOpen?"Interested Available":"Time Out"}</span>}</div></div>
      </div>
      <div className="rounded-3 border border-warning bg-warning-subtle p-3 mb-2"><strong><i className="bi bi-info-circle-fill me-2"></i>{redWorkerInstruction(note)}</strong></div>
      {note.VideoUrl && <div className="rounded-3 border bg-dark p-2 mb-2"><div className="d-flex justify-content-between align-items-center gap-2 mb-2"><span className="small text-white-50"><i className="bi bi-play-circle me-1"></i>Process Video{note.VideoVersion ? ` · Version ${note.VideoVersion}` : ""}</span><a className="btn btn-sm btn-outline-light" href={redVideoUrl(note.VideoUrl)} target="_blank" rel="noreferrer">Open Video</a></div><video className="w-100 rounded" style={{maxHeight:320}} src={redVideoUrl(note.VideoUrl)} controls preload="metadata">Your browser does not support video playback.</video></div>}
      <div className="small text-muted d-flex flex-wrap gap-3">
        {note.CrewRole && <span><i className="bi bi-people me-1"></i>Aapka role: <strong>{note.CrewRole === "PRIMARY" ? "Main Worker" : "Backup Worker"}</strong></span>}
        {Number(note.TargetQuantity) > 0 && <span><i className="bi bi-bullseye me-1"></i>Target: <strong>{note.TargetQuantity}</strong></span>}
        {note.ReceivingSupervisor && <span><i className="bi bi-person-badge me-1"></i>Supervisor: <strong>{note.ReceivingSupervisor}</strong></span>}
      </div>
      {note.ArrivalAt && <div className="small text-success mt-2"><i className="bi bi-check-circle-fill me-1"></i>Arrival confirmed: {new Date(note.ArrivalAt).toLocaleString("en-IN")}</div>}
      {note.EarningStatus && <div className="small mt-2">Earning: {note.EarningStatus}{note.ApprovedEarning != null ? ` · ₹${Number(note.ApprovedEarning).toFixed(2)}` : ""}</div>}
      {!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(note.Status) && interestWindowOpen && !isRedApplicationFull(note) && <div className="d-grid d-sm-flex justify-content-sm-end mt-3"><button className="btn btn-danger px-4 py-2 fw-bold" onClick={() => registerInterest(note)}><i className="bi bi-hand-index-thumb me-2"></i>Mujhe Kaam Karna Hai</button></div>}
      {["INTERESTED","HOME_APPROVED"].includes(String(note.interest_status||"").toUpperCase()) && ["PUBLISHED","HOME_APPROVAL","SELECTION"].includes(String(note.Status||"").toUpperCase()) && <div className="d-grid d-sm-flex justify-content-sm-end mt-3"><button className="btn btn-outline-primary px-4" onClick={()=>changeRedAvailability(note)}><i className="bi bi-clock-history me-2"></i>Change Available Time</button></div>}
      {!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(note.Status) && timedOut && <div className="alert alert-secondary mt-3 mb-0 py-2"><i className="bi bi-clock-history me-2"></i><strong>Time Out:</strong> Is slot ke liye Interested ka time khatam ho gaya hai.</div>}
    </article>;
  };

  const getExactLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Geolocation is not supported by your browser.");
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
          () => reject("Please allow Location/GPS permission to Punch In."),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    });
  };

  // useEffect(() => {
  //   const fetchTotalAmount = async () => {
  //     if (!user?.EmployeeID) return;

  //     try {
  //       const response = await axiosInstance.get(`/api/worker-total-amount/${user.EmployeeID}`);
  //       setTotalAmount(response.data.TotalAmount || 0); // Store total amount
  //     } catch (error) {
  //       console.error("Error fetching total amount:", error);
  //     }
  //   };

  //   fetchTotalAmount();
  // }, [user?.EmployeeID]);

  ////new code for worker data
  // useEffect(() => {
  //   const fetchWorkerData = async () => {
  //     try {
  //       const response = await axiosInstance.get("api/worker-wages");
  //       const filteredData = response.data.filter(
  //         (item) => item.EmployeeID === user?.EmployeeID
  //       );
  //       setWorkerData(filteredData);
  //     } catch (error) {
  //       console.error("Error fetching worker wages data:", error);
  //     }
  //   };

  //   fetchWorkerData();

  //   const handleResize = () => {
  //     setIsMobile(window.innerWidth <= 768);
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, [user?.EmployeeID]);
  // useEffect(() => {
  //   const fetchWorkerData = async () => {
  //     try {
  //       const response = await axiosInstance.get("api/worker-wages");
  //       let filteredData = response.data.filter(
  //         (item) => item.EmployeeID === user?.EmployeeID
  //       );

  //       if (startDate && endDate) {
  //         const start = new Date(startDate);
  //         const end = new Date(endDate);
  //         filteredData = filteredData.filter(item => {
  //           const date = new Date(item.Payroll_Entry_Date);
  //           return date >= start && date <= end;
  //         });
  //       }

  //       setWorkerData(filteredData);
  //     } catch (error) {
  //       console.error("Error fetching worker wages data:", error);
  //     }
  //   };

  //   fetchWorkerData();

  //   const handleResize = () => {
  //     setIsMobile(window.innerWidth <= 768);
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, [user?.EmployeeID, startDate, endDate]);

  const currentDate = new Date();

  // For the top Box (Total Amount)
  const [boxMonth, setBoxMonth] = useState(currentDate.getMonth() + 1);
  const [boxYear, setBoxYear] = useState(currentDate.getFullYear());

  // For the Table (Lotwise)
  const [tableFromDate, setTableFromDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
  );
  const [tableToDate, setTableToDate] = useState(currentDate);

  useEffect(() => {
    /*const fetchWorkerData = async () => {
      try {
        const response = await axiosInstance.get("api/worker-wages");
        let filteredData = response.data.filter(
          (item) => item.EmployeeID === user?.EmployeeID
        );

        // Normalize dates to prevent timezone mismatches
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0); // Set time to midnight
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); // Include the full end day

          filteredData = filteredData.filter((item) => {
            const itemDate = new Date(item.Payroll_Entry_Date);
            return itemDate >= start && itemDate <= end;
          });
        }

        // Apply the item filter if a specific item is selected (not "All")
        if (selectedItem && selectedItem !== "All") {
          filteredData = filteredData.filter(
            (item) => item["ITEM NAME"] === selectedItem
          );
        }
        setWorkerData(filteredData);

        // Update total amount based on filtered data
        const total = filteredData.reduce((sum, item) => sum + item.AMOUNT, 0);
        setTotalAmount(total.toFixed(2)); // Keep 2 decimal places

        // Calculate total quantity
        const qtyTotal = filteredData.reduce(
          (sum, item) => sum + Number(item.QTY),
          0
        );
        setTotalQty(qtyTotal);

        const handleResize = () => {
          setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      } catch (error) {
        console.error("Error fetching worker wages data:", error);
      }
    };*/

    const fetchMonthlySummary = async () => {
      try {
        if (!user?.EmployeeID) return;

        setIsSalaryLoading(true);
        const response = await axiosInstance.get(
          `/api/worker-salary/worker/${user.EmployeeID}/summary?month=${boxMonth}&year=${boxYear}`
        );
        const summary = response.data || {};
        setSalarySummary(summary);
        setTotalAmount(summary.GrossWorkAmount ?? summary.TotalEarned ?? 0);
        setTotalQty(summary.TotalQty || 0);
      } catch (error) {
        console.error("Error fetching monthly summary:", error);
        setSalarySummary({
          CompanyWorkAmount: 0,
          ReworkAmount: 0,
          GrossWorkAmount: 0,
          TotalEarned: 0,
          TotalQty: 0,
          AdvanceDeduction: 0,
          AdvancesDeducted: 0,
          TicketDeduction: 0,
          Penalties: 0,
          DebitFormDeduction: 0,
          TotalDeduction: 0,
          OutstandingAdvanceBalance: 0,
          FinalNetPayable: 0,
          PaymentStatus: "Unavailable",
          AdvanceDetails: [],
          TicketDetails: [],
          DebitFormDetails: [],
        });
      } finally {
        setIsSalaryLoading(false);
      }
    };

    fetchMonthlySummary();
  }, [user?.EmployeeID, boxMonth, boxYear]);

  useEffect(() => {
    const fetchLotWiseData = async () => {
      try {
        if (!user?.EmployeeID) return;
        const selectedFromDate = formatDateForApi(tableFromDate);
        const selectedToDate = formatDateForApi(tableToDate);
        if (!selectedFromDate || !selectedToDate || tableFromDate > tableToDate) {
          setAllLotWiseData([]);
          setLotWiseData([]);
          return;
        }

        const response = await axiosInstance.get(
          `/api/user-monthly-lotwise/${user.EmployeeID}?fromDate=${encodeURIComponent(selectedFromDate)}&toDate=${encodeURIComponent(selectedToDate)}`
        );

        setAllLotWiseData(response.data);   // master
        setLotWiseData(response.data);      // initial view
        setCurrentPage(1);
      } catch (error) {
        console.error("Lotwise fetch error:", error);
      }
    };

    fetchLotWiseData();
  }, [user?.EmployeeID, tableFromDate, tableToDate]);

  const totalPages = Math.ceil(lotWiseData.length / itemsPerPage);
  const paginatedData = lotWiseData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const lotWiseAmountTotal = lotWiseData.reduce(
    (sum, row) => sum + (Number(row.AMOUNT) || 0),
    0
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  //end

  const getISTTime = () => {
    const now = new Date();
    // Convert the time to IST
    const offset = 5.5 * 60 * 60 * 1000; // IST is UTC + 5:30
    const istTime = new Date(now.getTime() + offset);
    return istTime.toISOString().replace("T", " ").slice(0, 19); // Format as 'YYYY-MM-DD HH:MM:SS'
  };

  /// 3 Dec 24 //  yogesh
  // Fetch user department
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get("/api/AllUsers");
        const users = response.data;

        const userDepartment = users.find(
          (u) => u.EmployeeID === user?.EmployeeID
        )?.Department;
        if (userDepartment) {
          setDepartment(userDepartment);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchTemporaryDepartments = async () => {
      try {
        const response = await axiosInstance.get("/api/temporaryDepartments");
        const temporaryData = response.data;

        const tempDept = temporaryData.find(
          (temp) => temp.EmployeeID === user?.EmployeeID
        )?.TemporaryDepartment;
        if (tempDept) {
          setTemporaryDepartment(tempDept);
        } else {
          setTemporaryDepartment("Not Assigned");
        }
      } catch (error) {
        console.error("Error fetching temporary departments:", error);
      }
    };

    fetchUsers();
    fetchTemporaryDepartments();
  }, [user?.EmployeeID]);
  //end
  useEffect(() => {
    const checkSessionExpiration = () => {
      const sessionExpiration = localStorage.getItem("sessionExpiration");
      const currentTime = new Date();

      if (!sessionExpiration || currentTime > new Date(sessionExpiration)) {
        // handleLogout(); // Automatically logout if session expires
        window.location.reload(); // Reload the page after logout
      }
    };

    // Check session expiration immediately
    checkSessionExpiration();

    // Optionally, check periodically (e.g., every 5 seconds)
    const interval = setInterval(checkSessionExpiration, 5000);

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  // Handle "IN" Button Click
  const handleMarkIn = async () => {
    const loginTime = getISTTime();

    try {
      // Check for active session using /presentEmployees API
      const activeSessionResponse = await axiosInstance.get(
        "/api/presentEmployees"
      );
      const activeEmployees = activeSessionResponse.data;

      // Check if the user is already logged in
      const isActive = activeEmployees.some(
        (employee) => employee.EmployeeID === user?.EmployeeID
      );

      if (isActive) {
        showAlert("You are already marked in.", "danger");
        return; // Stop further processing
      }

      let userLocation = { latitude: null, longitude: null };
      try {
        userLocation = await getExactLocation();
      } catch (locErr) {
        showAlert(locErr, "danger");
        return; 
      }

      // Proceed with marking IN if no active session exists
      const finalDepartment = department || user?.Department;

      if (!finalDepartment || finalDepartment.trim() === "" || finalDepartment.trim().toLowerCase() === "not assigned") {
        showAlert("Department is compulsory. Please contact your admin to assign your department before Punch In.", "danger");
        return;
      }

      await axiosInstance.post("/api/mark-in", {
        EmployeeID: user?.EmployeeID,
        LoginTime: loginTime,
        Department: finalDepartment,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude
      });
      showAlert("Marked In successfully!", "success");
    } catch (err) {
      console.error("Error marking login time:", err);
      //alert("Failed to record login time. Please try again.");
      const errorMsg = err.response?.data?.error || "Failed to record login time. Please try again.";
      showAlert(errorMsg, "danger");
      alert(errorMsg);
    }
  };

  // Handle "OUT" Button Click
  const handleMarkOut = async () => {
    try {
      // Check for active session using /presentEmployees API
      const activeSessionResponse = await axiosInstance.get(
        "/api/presentEmployees"
      );
      const activeEmployees = activeSessionResponse.data;

      // Check if the user has an active session
      const isActive = activeEmployees.some(
        (employee) => employee.EmployeeID === user?.EmployeeID
      );

      if (!isActive) {
        showAlert("No active session found. You are not marked in.", "danger");
        return; // Stop further processing
      }

      if (!window.confirm("Are you sure you want to mark out?")) {
        return;
      }

      // Proceed with marking OUT if an active session exists
      const logoutTime = getISTTime();
      const deviceToken = localStorage.getItem("deviceToken");
      await axiosInstance.post("/api/logout", {
        EmployeeID: user?.EmployeeID,
        logoutTime: logoutTime, // Optional: Can be handled by the backend as well
        deviceToken: deviceToken
      });

      showAlert("Marked Out successfully!", "success");

      // Clear local storage and navigate to login page (if needed)
      // localStorage.removeItem("user");
      // localStorage.removeItem("loginTime");
      // navigate("/"); // Redirect to login page (optional)
    } catch (err) {
      console.error("Error marking logout time:", err);
      showAlert("Failed to record logout time. Please try again.", "danger");
    }
  };

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${
          type === "success" ? "Success!" : "Error!"
        }</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 3000);
  };

  /*useEffect(() => {
    const fetchUserItems = async () => {
      try {
        const response = await axiosInstance.get("api/user-items");
        // Filter for the logged in user using EmployeeID from AuthContext
        const userItems = response.data.filter(
          (item) => item.EmployeeID === user?.EmployeeID
        );
        // Assuming your API already returns unique rows,
        // map to an array of item names.
        const items = userItems.map((item) => item["ITEM NAME"]);
        setUniqueItems(items);
      } catch (error) {
        console.error("Error fetching user items:", error);
      }
    };

    if (user?.EmployeeID) {
      fetchUserItems();
    }
  }, [user?.EmployeeID]);*/

  useEffect(() => {
    if (!allLotWiseData.length) {
      setUniqueItems([]);
      return;
    }

    const items = [
      ...new Set(allLotWiseData.map((item) => item.ITEM_NAME)),
    ];

    setUniqueItems(items);
  }, [allLotWiseData]);

  useEffect(() => {
    setCurrentPage(1);
    if (selectedItem === "All") {
      setLotWiseData(allLotWiseData);
    } else {
      const filtered = allLotWiseData.filter(
        (item) => item.ITEM_NAME === selectedItem
      );
      setLotWiseData(filtered);
    }
  }, [selectedItem, allLotWiseData]);

  const columns = [
    {
      name: "ITEM NAME",
      selector: (row) => row.ITEM_NAME,
      sortable: true,
    },
    {
      name: "PROCESS NAME",
      selector: (row) => row.PROCESS_NAME,
      sortable: true,
    },
    {
      name: "RATE",
      selector: (row) => (row.AMOUNT && row.QTY && row.QTY > 0 ? (row.AMOUNT / row.QTY).toFixed(2) : "0.00"),
      sortable: true,
    },
    {
      name: "QTY",
      selector: (row) => row.QTY,
      sortable: true,
    },
    {
      name: "AMOUNT",
      selector: (row) => row.AMOUNT,
      sortable: true,
    },
  ];

  /*const columns = [
    {
      name: "LOT NO",
      selector: (row) => row.LOT_NO,
      sortable: true,
    },
    {
      name: "ITEM NAME",
      selector: (row) => row.ITEM_NAME,
      sortable: true,
    },
    {
      name: "PROCESS",
      selector: (row) => row.PROCESS_NAME,
    },
    {
      name: "QTY",
      selector: (row) => row.QTY,
    },
    {
      name: "AMOUNT",
      selector: (row) => row.AMOUNT,
    },
    {
      name: "DATE",
      selector: (row) =>
        row.CREATED_ON
          ? new Date(row.CREATED_ON).toLocaleDateString("en-GB")
          : "N/A",
    },
  ];*/

  return (
    <div className=" ">
      <div id="alertPlaceholder"></div>
      <nav className="navbar navbar-expand-lg bg-white sticky-top" style={{ boxShadow: "0 1px 5px rgba(0, 0, 0, 0.2)" }}>
        <div className="container-fluid flex-wrap px-2 px-sm-4">
          <a className="navbar-brand me-2" href="/User-dashboard">
            <img src={Logo} className="img-fluid" alt="Fibre Bond Industries" style={{ height: isMobile ? "34px" : "40px" }} />
          </a>

          <div className="d-flex align-items-center gap-2 ms-auto order-lg-3">
            <button type="button" className="btn border rounded-circle position-relative d-flex align-items-center justify-content-center" style={{ width: "44px", height: "44px", backgroundColor: "#eff6ff", borderColor: "#bfdbfe", boxShadow: "0 2px 8px rgba(37, 99, 235, 0.16)" }} onClick={openRedNotifications} aria-label={`Open RED process notifications${unreadRedNotificationCount ? `, ${unreadRedNotificationCount} unread` : ""}`}>
              <i className="bi bi-bell-fill fs-5" style={{ color: "#2563eb" }}></i>
              {unreadRedNotificationCount > 0 && <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white" style={{ minWidth: 22 }}>{unreadRedNotificationCount > 99 ? "99+" : unreadRedNotificationCount}</span>}
            </button>
            <span className="d-none d-md-inline text-nowrap">Welcome, {user?.Name}!</span>
            <div className="dropdown">
              <button className="btn p-0 border-0 dropdown-toggle d-flex align-items-center" type="button" id="workerProfileMenu" data-bs-toggle="dropdown" aria-expanded="false">
                <img src={User} alt="Profile" className="rounded-circle" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="workerProfileMenu">
                <li><button type="button" className="dropdown-item text-danger" onClick={logout}>Logout</button></li>
              </ul>
            </div>
            <button className="navbar-toggler border-0 px-1" type="button" data-bs-toggle="collapse" data-bs-target="#workerDashboardNav" aria-controls="workerDashboardNav" aria-expanded="false" aria-label="Toggle navigation"><span className="navbar-toggler-icon"></span></button>
          </div>

          <div className="collapse navbar-collapse order-lg-2 flex-grow-1" id="workerDashboardNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item ms-lg-2">
                <a className="nav-link active" href="/User-Tickets" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ color: hover ? "#ff0000" : "inherit", fontWeight: "600" }}><i className="bi bi-ticket-detailed me-1"></i> TICKET</a>
              </li>
              <li className="nav-item ms-lg-2">
                <a className="nav-link active" href="/wip-worker-app" style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px", letterSpacing: "0.5px" }}><i className="bi bi-person-workspace me-2"></i> TASK TRACKER</a>
              </li>
              {canViewWorkbookLots && (
                <li className="nav-item ms-lg-2">
                  <a className="nav-link active" href="/cutting-lot-details" style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px", letterSpacing: "0.5px" }}><i className="bi bi-scissors me-2" aria-hidden="true"></i>CUTTING LOTS</a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
      {redNotificationModalOpen && <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="redWorkerNotificationsTitle" style={{ backgroundColor: "rgba(15, 23, 42, 0.62)", zIndex: 2000 }}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable mx-auto my-2 my-sm-4" style={{ width: "calc(100% - 16px)", maxWidth: "1180px", minHeight: 0 }}>
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" style={{ maxHeight: "calc(100vh - 16px)" }}>
            <div className="modal-header bg-primary text-white px-3 px-sm-4 py-3 py-sm-4">
              <div><h5 className="modal-title mb-1" id="redWorkerNotificationsTitle"><i className="bi bi-bell-fill me-2"></i>Kaam Ki Suchna</h5><div className="small opacity-75">Naya lot aur aapke kaam ki jankari</div></div>
              <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => setRedNotificationModalOpen(false)}></button>
            </div>
            <div className="modal-body bg-light p-2 p-sm-4">
              <div className="row g-2 mb-3">
                <div className="col-6"><button type="button" className={`w-100 h-100 rounded-3 border p-2 p-sm-3 text-start ${redNotificationView === "active" ? "bg-danger text-white border-danger shadow-sm" : "bg-white text-dark"}`} onClick={() => { setRedNotificationView("active"); setRedNotificationSearch(""); }}><div className="d-flex align-items-center justify-content-between gap-1"><span className="fw-bold"><i className="bi bi-briefcase-fill me-1 me-sm-2"></i>Abhi Ka Kaam</span><span className={`badge rounded-pill ${redNotificationView === "active" ? "bg-white text-danger" : "bg-danger"}`}>{activeRedNotifications.length}</span></div><div className={`small mt-1 ${redNotificationView === "active" ? "text-white-50" : "text-muted"}`}>Jis par action lena hai</div></button></div>
                <div className="col-6"><button type="button" className={`w-100 h-100 rounded-3 border p-2 p-sm-3 text-start ${redNotificationView === "history" ? "bg-secondary text-white border-secondary shadow-sm" : "bg-white text-dark"}`} onClick={() => { setRedNotificationView("history"); setRedNotificationSearch(""); }}><div className="d-flex align-items-center justify-content-between gap-1"><span className="fw-bold"><i className="bi bi-check-circle-fill me-1 me-sm-2"></i>Purana Kaam</span><span className={`badge rounded-pill ${redNotificationView === "history" ? "bg-white text-secondary" : "bg-secondary"}`}>{redNotificationHistory.length}</span></div><div className={`small mt-1 ${redNotificationView === "history" ? "text-white-50" : "text-muted"}`}>Poora ya band ho gaya</div></button></div>
              </div>
              <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 mb-3">
                <div className="input-group input-group-lg flex-grow-1 shadow-sm">
                  <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                  <input type="search" className="form-control border-start-0" placeholder="Lot number ya department khojein..." value={redNotificationSearch} onChange={event => setRedNotificationSearch(event.target.value)} autoComplete="off" />
                  {redNotificationSearch && <button type="button" className="btn btn-outline-secondary" onClick={() => setRedNotificationSearch("")} aria-label="Clear search"><i className="bi bi-x-lg"></i></button>}
                </div>
                <div className="small text-muted text-nowrap px-1"><strong>{visibleRedNotifications.length}</strong> suchna</div>
              </div>
              {visibleRedNotifications.length
                ? visibleRedNotifications.map(renderRedNotification)
                : <div className="text-center text-muted bg-white border rounded-4 p-4 p-sm-5"><i className={`bi ${redNotificationSearch ? "bi-search" : "bi-bell-slash"} fs-1 d-block mb-2`}></i><strong className="d-block fs-5">{redNotificationSearch ? "Koi lot nahi mila" : redNotificationView === "active" ? "Abhi koi naya kaam nahi hai" : "Purana kaam nahi hai"}</strong><span className="small">{redNotificationSearch ? "Dusra lot number ya department likhein." : redNotificationView === "active" ? "Naya kaam aayega to bell par number dikhega." : "Poora hua kaam yahan dikhega."}</span></div>}
            </div>
          </div>
        </div>
      </div>}
      {redTimeModal && <RedWorkerTimeModal
        employeeId={user?.EmployeeID}
        mode={redTimeModal.mode}
        slot={redTimeModal.slot}
        onClose={() => setRedTimeModal(null)}
        onSaved={handleRedTimeSaved}
      />}
      {/* <main>
        <div className="container">
          <p>Employee ID: {user?.EmployeeID}</p>
          <p>Department: {department}</p>
          <p>Assigned Department: {temporaryDepartment}</p>
        </div>
      </main> */}
      <div
        className="dashboard-container"
        style={{ background: "#f7f9fc", height: "-webkit-fill-available" }}
      >
        <main style={{ padding: "20px" }}>
          <div
            className="dashboard-content"
            style={{ maxWidth: "800px", margin: "0 auto" }}
          >
            <div className="d-none" aria-hidden="true">
              <div className="card-body">
                <h5 className="text-danger mb-3">New Lot for Work</h5>
                {redSupportNotifications.length > 0 ? redSupportNotifications.map(note => <div className="border rounded p-3 mb-2" key={note.notification_id}><strong>Lot ID: {note.DisplayLotId || note.LotId || "Not available"} - Workers Required</strong><p className="mb-2 small">{note.Message}</p><div className="small text-muted mb-2">Department: {note.Department || "-"} · Status: {note.Status || "-"}{Number(note.TargetQuantity) > 0 ? ` · Target: ${note.TargetQuantity}` : ""}{note.CrewRole ? ` · Assignment: ${note.CrewRole}` : ""}</div><div className="d-flex justify-content-between align-items-center"><span className={`badge ${note.interest_status === "HOME_DECLINED" || note.interest_status === "CANCELLED" ? "bg-danger" : note.interest_status === "SELECTED" ? "bg-success" : "bg-warning text-dark"}`}>{note.interest_status || "Open"}</span>{!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(note.Status) && <button className="btn btn-danger btn-sm" onClick={()=>registerInterest(note)}>Interested</button>}</div>{note.ArrivalAt && <div className="small text-success mt-2">Arrival confirmed: {new Date(note.ArrivalAt).toLocaleString("en-IN")}</div>}{note.EarningStatus && <div className="small mt-2">Earning: {note.EarningStatus}{note.ApprovedEarning != null ? ` · ₹${Number(note.ApprovedEarning).toFixed(2)}` : ""}</div>}</div>) : <div className="text-muted">No new lot notification available right now.</div>}
              </div>
            </div>
            <div
              className="card"
              style={{
                background: "#ffffff",
                // background: "linear-gradient(90deg,orange, orange,orange, white,white, white,green, green, green)",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: "20px",
                position: "relative"
              }}
            >
              <div style={{ position: "absolute", top: "20px", right: "20px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {/* Month Filter Dropdown */}
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={boxMonth}
                  onChange={(e) => setBoxMonth(Number(e.target.value))}
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('default', { month: 'short' })}
                    </option>
                  ))}
                </select>

                {/* Year Filter Dropdown */}
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={boxYear}
                  onChange={(e) => setBoxYear(Number(e.target.value))}
                >
                  {[...Array(10)].map((_, i) => {
                    const year = new Date().getFullYear() - 5 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              <span style={{ marginBottom: "40px", color: "#ff0000", textAlign: "center", display: "block" }}>Please Don't Forget To Logout After Complete Your Work</span>
              <div style={{ backgroundColor: "#e9f2ff", padding: "10px", borderRadius: "5px", marginBottom: "15px", borderLeft: "4px solid #0d6efd" }}>
                <strong style={{ color: "#0d6efd" }}>Showing Month-Wise Data For: {new Date(0, boxMonth - 1).toLocaleString('default', { month: 'long' })} {boxYear}</strong>
              </div>
              {isSalaryLoading ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5" style={{ minHeight: "360px" }} role="status" aria-live="polite">
                  <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} aria-hidden="true"></div>
                  <strong className="fs-5 text-primary">Loading salary data...</strong>
                  <span className="text-muted mt-1">Please wait while the latest month-wise summary is loading.</span>
                </div>
              ) : <>
              <p
                style={{
                  fontSize: "18px",
                  color: "black",
                  marginBottom: "8px",
                }}
              >
                <strong>Employee ID:</strong> {user?.EmployeeID}
              </p>
              <p
                style={{
                  fontSize: "18px",
                  color: "black",
                  marginBottom: "8px",
                }}
              >
                <strong>Department:</strong> {department}
              </p>
              <p
                style={{
                  fontSize: "18px",
                  color: "black",
                  marginBottom: "8px",
                }}
              >
                <strong>Assigned Department:</strong>{" "}
                {temporaryDepartment}
              </p>
              <p style={{ fontSize: "18px", color: "#0d6efd", margin: "14px 0 8px" }}><strong>Earnings</strong></p>
              <p style={{ fontSize: "18px", color: "black", marginBottom: "8px" }}>
                <strong>Company Work Amount:</strong> {formatCurrency(salarySummary.CompanyWorkAmount)}
              </p>
              <p style={{ fontSize: "18px", color: "black", marginBottom: "8px" }}>
                <strong>Rework Amount:</strong> {formatCurrency(salarySummary.ReworkAmount)}
              </p>
              <p style={{ fontSize: "18px", color: "#0d6efd", marginBottom: "8px" }}>
                <strong>Gross Work Amount:</strong> {formatCurrency(salarySummary.GrossWorkAmount ?? totalAmount)}
              </p>
              <p style={{ fontSize: "18px", color: "black", marginBottom: "8px" }}>
                <strong>Total Quantity:</strong> {totalQty}
              </p>

              <p style={{ fontSize: "18px", color: "#c43d3d", margin: "14px 0 8px" }}><strong>Deductions</strong></p>
              <p style={{ fontSize: "18px", color: "black", marginBottom: "8px" }}>
                <strong>Advance Amount (Outstanding Balance):</strong> {formatCurrency(salarySummary.OutstandingAdvanceBalance)}
              </p>
              <p style={{ fontSize: "18px", color: "#c43d3d", marginBottom: "8px" }}>
                <strong>Advance Deduction:</strong> -{formatCurrency(salarySummary.AdvanceDeduction ?? salarySummary.AdvancesDeducted)}
              </p>
              <p style={{ fontSize: "18px", color: "#c43d3d", marginBottom: "8px" }}>
                <strong>Ticket Issued Deduction:</strong> -{formatCurrency(salarySummary.TicketDeduction ?? salarySummary.Penalties)}
              </p>
              <p style={{ fontSize: "18px", color: "#c43d3d", marginBottom: "8px" }}>
                <strong>Debit Form Deduction:</strong> -{formatCurrency(salarySummary.DebitFormDeduction)}
              </p>
              <p style={{ fontSize: "18px", color: "#c43d3d", marginBottom: "8px" }}>
                <strong>Total Deduction:</strong> -{formatCurrency(salarySummary.TotalDeduction)}
              </p>
              <p style={{ fontSize: "18px", color: "black", marginBottom: "8px" }}>
                <strong>Status:</strong>{" "}
                <span className={`badge ${Number(salarySummary.TotalDeduction || 0) > 0 ? "bg-success" : "bg-secondary"}`}>
                  {isSalaryLoading ? "Loading..." : salarySummary.PaymentStatus}
                </span>
              </p>
              <p style={{ fontSize: "21px", color: "#2f7d4a", marginBottom: "12px" }}>
                <strong>Net Payment:</strong> {formatCurrency(salarySummary.FinalNetPayable)}
              </p>
              </>}
              {/* Buttons for IN and OUT */}
              <div
                style={{ display: "flex", justifyContent: "end", gap: "20px" }}
              >
                <button className="btn btn-success" onClick={handleMarkIn}>
                  IN
                </button>
                <button className="btn btn-danger" onClick={handleMarkOut}>
                  OUT
                </button>
              </div>
            </div>
          </div>

          <div className="container mt-4 d-flex flex-wrap responsive-date-picker">

            {/* Payroll Entry Date Range Filter */}
            <div className="dropdown mb-2 me-2">
              <label className="form-label">From Date: </label>&nbsp;
              <DatePicker
                className="form-control"
                selected={tableFromDate}
                onChange={(date) => setTableFromDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select from date"
                selectsStart
                startDate={tableFromDate}
                endDate={tableToDate}
                maxDate={tableToDate || undefined}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>

            <div className="dropdown mb-2 me-2">
              <label className="form-label">To Date: </label>&nbsp;
              <DatePicker
                className="form-control"
                selected={tableToDate}
                onChange={(date) => setTableToDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select to date"
                selectsEnd
                startDate={tableFromDate}
                endDate={tableToDate}
                minDate={tableFromDate || undefined}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>

            {/* Separate Item Filter Dropdown */}
            <div className="dropdown mb-2 me-2">
              <label className="form-label">Item Name: </label>&nbsp;
              <select
                className="form-control "
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
              >
                <option value="All">All</option>
                {uniqueItems.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            {/* Reset Button */}
            <div className="mb-2" style={{ marginTop: "auto" }}>
              <button
                className="btn btn-secondary mt-4"
                onClick={() => {
                  setSelectedItem("All");
                  const today = new Date();
                  setTableFromDate(new Date(today.getFullYear(), today.getMonth(), 1));
                  setTableToDate(today);
                }}
              >
                Reset
              </button>
            </div>
          </div>
          <div
            className="container mt-4"
            style={{
              boxShadow: "0 1px 5px rgba(0, 0, 0, 0.51)",
              borderRadius: "5px",
              padding: "6px",
            }}
          >
            {!isMobile ? (
              <DataTable
                columns={columns}
                data={lotWiseData}
                pagination
                highlightOnHover
                noDataComponent="There are no records to display"
              />
            ) : (
              <>
                <div className="row">
                  {paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <div className="col-12 mb-3 mt-2" key={index}>
                        <div className="card p-3 shadow-sm">
                          <h5 className="card-title">{item.ITEM_NAME}</h5>

                          <p className="card-text">
                            <strong>Worker:</strong> {item.WORKER_NAME}
                          </p>

                          <p className="card-text">
                            <strong>Quantity:</strong> {item.QTY}
                          </p>

                          <p className="card-text">
                            <strong>Amount:</strong> ₹{item.AMOUNT}
                          </p>

                          <p className="card-text">
                            <strong>Period:</strong>{" "}
                            {new Date(item.Period_Start).toLocaleDateString("en-GB")} -{" "}
                            {new Date(item.Period_End).toLocaleDateString("en-GB")}
                          </p>

                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center mt-3">
                      <p>No data found.</p>
                    </div>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between mt-3">
                    <button
                      className="btn btn-primary mb-3"
                      onClick={prevPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="btn btn-primary mb-3"
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
            {lotWiseData.length > 0 && (
              <div className="d-flex justify-content-end border-top px-3 py-3 mt-1">
                <strong style={{ minWidth: isMobile ? "auto" : "20%", textAlign: "left" }}>
                  Total Amount: {formatCurrency(lotWiseAmountTotal)}
                </strong>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default UserDashboard;
